'use client';

import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@mui/material';
import type { NavigationItem, NavigationType } from '@/interfaces/component/navigation.interface';
import { useToastStore } from '@/stores/toast.store';
import theme from '@/app/theme';

const navigationButtonStyle = {
  GLOBAL: {
    paddingX: '14px',
    paddingY: '6px',
    justifyContent: 'left'
  },
  SIDEBAR: {
    paddingX: '16px',
    paddingY: '10px',
    justifyContent: 'left',
    color: theme.palette.text.primary
  }
};

const NavigationButton = ({ navigationItem, onAccessDenied, navigationType }: { navigationItem: NavigationItem; onAccessDenied?: () => void; navigationType: NavigationType }) => {
  const router = useRouter();
  const pathname = usePathname();

  const { setToastInfos, setShowToast } = useToastStore();

  const { label, path, checkBeforeAccess = () => true } = navigationItem;

  // 리뷰 버튼을 위한 특별 처리
  const isReviewButton = label === '리뷰 리스트';

  const isExternalLink = path.startsWith('http://') || path.startsWith('https://');

  const isNavigationToCurrentPage = isReviewButton
    ? pathname.includes('/reviews') // 리뷰 버튼은 리뷰 페이지에서만 비활성화
    : path === '/dashboard'
      ? path === pathname || pathname === '/stores/-1/dashboard'
      : path === pathname;

  return (
    <Button
      onClick={() => {
        const accessable = checkBeforeAccess();

        if (!accessable) {
          if (onAccessDenied) {
            onAccessDenied();
          } else {
            setToastInfos({ text: '페이지에 접근할 수 없습니다.' });
            setShowToast(true);
          }
        } else {
          if (isExternalLink) {
            // 메디컨텐츠 메뉴는 새창으로 열기
            if (label === '메디컨텐츠') {
              window.open(path, '_blank');
            } else {
              window.location.href = path;
            }
          } else {
            router.push(path);
          }
        }
      }}
      fullWidth
      sx={{
        color: theme.palette.text[navigationType === 'GLOBAL' ? 'secondary' : 'primary'],
        ':disabled': {
          borderRadius: '12px',
          backgroundColor: 'var(--Gray-Gray-50, #F9FAFB)',
          color: theme.palette.gray[900],
          fontWeight: 700
        },
        ...navigationButtonStyle[navigationType]
      }}
      color="info"
      disabled={isNavigationToCurrentPage}
    >
      {label}
    </Button>
  );
};

export default NavigationButton;
